import { useCallback, useEffect, useRef, useState } from 'react'

import { CaseClassificationResult, classifyCaseFromDescription } from './caseTaxonomyServices'

export type ClassificationStatus = 'idle' | 'classifying' | 'done' | 'error'

export const CLASSIFICATION_STATUS = {
  idle: 'idle',
  classifying: 'classifying',
  done: 'done',
  error: 'error',
} satisfies Record<ClassificationStatus, ClassificationStatus>

const CLASSIFICATION_DEBOUNCE_MS = 1000

/**
 * Debounced AI classification of the case description. The description is a
 * live textarea (unlike the fresh app's save-on-close editor), so requests
 * wait for a typing pause; a requestId guards against stale responses
 * overwriting newer ones.
 */
export const useCaseClassification = (description: string, regionId?: string) => {
  const [status, setStatus] = useState<ClassificationStatus>('idle')
  const [result, setResult] = useState<CaseClassificationResult | null>(null)
  const requestIdRef = useRef(0)

  const classify = useCallback(async (text: string, homeRegionId: string) => {
    const requestId = ++requestIdRef.current
    setStatus('classifying')
    try {
      const classification = await classifyCaseFromDescription(text, homeRegionId)
      if (requestId !== requestIdRef.current) return
      setResult(classification)
      setStatus('done')
    } catch {
      if (requestId !== requestIdRef.current) return
      setResult(null)
      setStatus('error')
    }
  }, [])

  const trimmedDescription = description.trim()

  useEffect(() => {
    if (!trimmedDescription || !regionId) {
      requestIdRef.current += 1
      setStatus('idle')
      setResult(null)
      return
    }
    const debounce = setTimeout(
      () => classify(trimmedDescription, regionId),
      CLASSIFICATION_DEBOUNCE_MS,
    )
    return () => clearTimeout(debounce)
  }, [trimmedDescription, regionId, classify])

  const retry = useCallback(() => {
    if (trimmedDescription && regionId) {
      classify(trimmedDescription, regionId)
    }
  }, [classify, trimmedDescription, regionId])

  return { status, result, retry }
}
